import type { SquawkPagesParams } from "@common/api/quoting/squawk"
import type { WorkPackageStatus } from "@common/client"
import { z } from "zod"
import {
  COLUMNS,
  type Column,
} from "@/routes/_layout/quoting_/squawks/-lib/columns"
import { squawkFilterConfigs } from "@/routes/_layout/quoting_/squawks/-lib/filters"
import {
  GROUPINGS,
  type Grouping,
} from "@/routes/_layout/quoting_/squawks/-lib/grouping"
import {
  type SquawksSearch,
  squawkPagesParams,
  squawksSearchSchema,
} from "@/routes/_layout/quoting_/squawks/-lib/search"
import { isSettled } from "./lifecycle"

const fields = squawksSearchSchema.shape

export const squawkSearchFields = {
  sq_search: fields.search,
  sq_status: fields.status,
  sq_severity: fields.severity,
  sq_reporter: fields.reporter,
  sq_ata: fields.ata,
  sq_group: fields.group,
  sq_subgroup: fields.subgroup,
  sq_columns: fields.columns,
  sq_sort: fields.sort,
  sq_direction: fields.direction,
}

const squawkTabSearchSchema = z.object(squawkSearchFields)

export type SquawkTabSearch = z.infer<typeof squawkTabSearchSchema>

export const WORK_ORDER_SQUAWK_COLUMNS: Column[] = COLUMNS.filter(
  (column) => column !== "work_order",
)

export const WORK_ORDER_SQUAWK_GROUPINGS: Grouping[] = GROUPINGS.filter(
  (grouping) => grouping !== "work_order" && grouping !== "customer",
)

export const workOrderSquawkFilterConfigs = squawkFilterConfigs.filter(
  (config) => config.key !== "work_order" && config.key !== "customer",
)

export function squawksSearchOf(
  search: SquawkTabSearch,
  status: WorkPackageStatus,
): SquawksSearch {
  return {
    tab: isSettled(status) ? "closed" : undefined,
    search: search.sq_search,
    status: search.sq_status,
    severity: search.sq_severity,
    reporter: search.sq_reporter,
    ata: search.sq_ata,
    group: search.sq_group ?? "none",
    subgroup: search.sq_subgroup,
    columns: search.sq_columns,
    sort: search.sq_sort,
    direction: search.sq_direction,
  }
}

export function workOrderSquawkParams(
  search: SquawksSearch,
  workOrderId: string,
): SquawkPagesParams {
  return { ...squawkPagesParams(search), work_package_id: workOrderId }
}

export function squawkTabPatch(
  patch: Partial<SquawksSearch>,
): Partial<SquawkTabSearch> {
  return {
    ...("search" in patch ? { sq_search: patch.search } : {}),
    ...("status" in patch ? { sq_status: patch.status } : {}),
    ...("severity" in patch ? { sq_severity: patch.severity } : {}),
    ...("reporter" in patch ? { sq_reporter: patch.reporter } : {}),
    ...("ata" in patch ? { sq_ata: patch.ata } : {}),
    ...("group" in patch ? { sq_group: patch.group } : {}),
    ...("subgroup" in patch ? { sq_subgroup: patch.subgroup } : {}),
    ...("columns" in patch ? { sq_columns: patch.columns } : {}),
    ...("sort" in patch ? { sq_sort: patch.sort } : {}),
    ...("direction" in patch ? { sq_direction: patch.direction } : {}),
  }
}
